import { MediaTableRow } from '@pages/Library/components/LibraryContentMediaTable/cells/types';

// Platform asset imports
import dropboxPng from '@assets/channel-pngs/dropbox.png';
import facebookPng from '@assets/channel-pngs/facebook.png';
import googledrivePng from '@assets/channel-pngs/googledrive.png';
import instagramPng from '@assets/channel-pngs/instagram.png';
import linkedinPng from '@assets/channel-pngs/linkedin.png';
import vimeoPng from '@assets/channel-pngs/vimeo.png';
import youtubePng from '@assets/channel-pngs/youtube.png';

type Platform = {
  key: string;
  name: string;
  displayName: string;
  icon: string | null;
  tooltipText: string | null;
  /**
   * Adding a pasted link posts the unfurl item's url instead of the pasted link. True for the
   * share platforms, whose unfurl hands back a re-resolvable link per file (Shade `?asset=`, Box
   * share-download, Frame.io viewer), so a share holding one video adds that video rather than
   * the share, which the single-video route refuses. False where the single-video unfurl returns
   * the acquisition URL (Google Drive's download link) and the pasted link is the stable one.
   * Mirrors the backend (`external.api.atlas`, `src/modules/media/helpers/`): the true side is
   * the share generators' `toVideoEntity` in `external/shade.ts`, `external/box.ts` and
   * `external/frame-io.ts`; the false side is `singleVideoUrlToDetails` in `externalUrl.ts`,
   * whose entity url every other provider's single-video unfurl reuses.
   */
  addsUnfurlItemUrl: boolean;
};

/**
 * Centralized platform configuration.
 * Not yet the single source of truth: icons, brand colours, hostname detection and the
 * supported-platforms copy are still listed by hand elsewhere (see #867).
 */
export const PLATFORMS = {
  youtube: {
    key: 'youtube',
    name: 'YouTube',
    displayName: 'YouTube',
    icon: youtubePng,
    tooltipText: 'View on YouTube',
    addsUnfurlItemUrl: false,
  },
  vimeo: {
    key: 'vimeo',
    name: 'Vimeo',
    displayName: 'Vimeo',
    icon: vimeoPng,
    tooltipText: 'View on Vimeo',
    addsUnfurlItemUrl: false,
  },
  linkedin: {
    key: 'linkedin',
    name: 'LinkedIn',
    displayName: 'LinkedIn',
    icon: linkedinPng,
    tooltipText: 'View on LinkedIn',
    addsUnfurlItemUrl: false,
  },
  instagram: {
    key: 'instagram',
    name: 'Instagram',
    displayName: 'Instagram',
    icon: instagramPng,
    tooltipText: 'View on Instagram',
    addsUnfurlItemUrl: false,
  },
  facebook: {
    key: 'facebook',
    name: 'Facebook',
    displayName: 'Facebook',
    icon: facebookPng,
    tooltipText: 'View on Facebook',
    addsUnfurlItemUrl: false,
  },
  googledrive: {
    key: 'googledrive',
    name: 'Google Drive',
    displayName: 'Google Drive',
    icon: googledrivePng,
    tooltipText: 'View on Google Drive',
    addsUnfurlItemUrl: false,
  },
  dropbox: {
    key: 'dropbox',
    name: 'Dropbox',
    displayName: 'Dropbox',
    icon: dropboxPng,
    tooltipText: 'View on Dropbox',
    addsUnfurlItemUrl: false,
  },
  frameio: {
    key: 'frameio',
    name: 'Frame.io',
    displayName: 'Frame.io',
    icon: null, // Uses FrameIoIcon SVG component (rendered in ExternalLinkIcon)
    tooltipText: 'View on Frame.io',
    addsUnfurlItemUrl: true,
  },
  box: {
    key: 'box',
    name: 'Box',
    displayName: 'Box',
    icon: null, // Uses BoxIcon SVG component (rendered in ExternalLinkIcon)
    tooltipText: 'View on Box',
    addsUnfurlItemUrl: true,
  },
  shade: {
    key: 'shade',
    name: 'Shade',
    displayName: 'Shade',
    icon: null, // Uses ShadeColorIcon SVG component (rendered in ExternalLinkIcon)
    tooltipText: 'View on Shade',
    addsUnfurlItemUrl: true,
  },
  tiktok: {
    key: 'tiktok',
    name: 'TikTok',
    displayName: 'TikTok',
    icon: null, // Uses TikTokIcon SVG component (rendered in ExternalLinkIcon)
    tooltipText: 'View on TikTok',
    addsUnfurlItemUrl: false,
  },
  soundcloud: {
    key: 'soundcloud',
    name: 'SoundCloud',
    displayName: 'SoundCloud',
    icon: null, // Uses SoundCloudIcon SVG component (rendered in ExternalLinkIcon)
    tooltipText: 'View on SoundCloud',
    addsUnfurlItemUrl: false,
  },
  generic: {
    key: 'generic',
    name: 'External',
    displayName: 'External',
    icon: null, // Will use OpenInNew icon
    tooltipText: 'View external link',
    addsUnfurlItemUrl: false,
  },
  upload: {
    key: 'upload',
    name: 'Upload',
    displayName: 'Upload',
    icon: null,
    tooltipText: null,
    addsUnfurlItemUrl: false,
  },
  local: {
    key: 'local',
    name: 'Local',
    displayName: 'Local',
    icon: null,
    tooltipText: null,
    addsUnfurlItemUrl: false,
  },
  uploading: {
    key: 'uploading',
    name: 'Uploading',
    displayName: 'Uploading',
    icon: null,
    tooltipText: null,
    addsUnfurlItemUrl: false,
  },
} as const satisfies Record<string, Platform>;

export type PlatformKey = keyof typeof PLATFORMS;

/**
 * Array of all platform display names for use in filters/dropdowns
 */
export const PLATFORM_NAMES = Object.values(PLATFORMS).map((p) => p.displayName);

/**
 * Maps backend extractor names to our platform keys.
 * The backend may use different names than our PLATFORMS keys
 * (e.g. "next.frame.io" for Frame.io).
 */
const EXTRACTOR_TO_PLATFORM: Record<string, PlatformKey> = {
  'next.frame.io': 'frameio',
};

/**
 * Get platform configuration by key.
 * Accepts both our platform keys ("frameio") and backend extractor names ("next.frame.io").
 */
export const getPlatformConfig = (key: string | undefined): (typeof PLATFORMS)[PlatformKey] => {
  if (!key) return PLATFORMS.generic;
  const lowered = key.toLowerCase();
  const normalizedKey = (EXTRACTOR_TO_PLATFORM[lowered] ?? lowered) as PlatformKey;
  return PLATFORMS[normalizedKey] || PLATFORMS.generic;
};

/**
 * Gets the display name for a media item's channel/source.
 * Handles external sources (YouTube, Vimeo, etc.), user uploads, and special cases.
 *
 * @param media - The media table row to get the channel name for
 * @returns A human-readable channel name
 */
export const getChannelName = (media: MediaTableRow): string => {
  // Handle uploading files or missing origin
  if (media.isUploading || !media.origin) {
    return PLATFORMS.uploading.displayName;
  }

  if (media.origin.source === 'external') {
    const type = media.origin.type?.toLowerCase();

    // Special cases: backend may store type as 'generic' for platforms it re-resolves via CDN URLs
    if (type === 'generic' && media.origin.url) {
      const lowerUrl = media.origin.url.toLowerCase();
      if (lowerUrl.includes('dropbox')) return PLATFORMS.dropbox.displayName;
      if (lowerUrl.includes('frame.io')) return PLATFORMS.frameio.displayName;
      if (lowerUrl.includes('boxcloud.com')) return PLATFORMS.box.displayName;
    }

    // Return the platform display name or fallback to generic
    const platformConfig = getPlatformConfig(type);
    return platformConfig.displayName;
  } else if (media.origin.source === 'user') {
    return media.origin.type === 'upload'
      ? PLATFORMS.upload.displayName
      : PLATFORMS.local.displayName;
  }

  return 'Unknown';
};

// YouTube / hostname helpers moved to ./youtube (PNG-free so they're unit-testable
// via tsx). Re-exported here so existing `@lib/utils/platforms` importers are unaffected.
export { extractYouTubeId, getYouTubeVideoId, isHostname } from './youtube';
